import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'
import type {
  ApiKey,
  ApiKeyListItem,
  ApiKeysListResponse,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
} from '@/data/types/apiKeys.types'

async function listApiKeys(skip: number = 0, limit: number = 100): Promise<ApiKeysListResponse> {
  const params = new URLSearchParams({
    skip: skip.toString(),
    limit: limit.toString(),
  })
  return fetchWithAuth(`/api/v1/api-keys?${params}`)
}

async function createApiKey(data: CreateApiKeyRequest): Promise<ApiKey> {
  return fetchWithAuth('/api/v1/api-keys', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function updateApiKey(apiKeyId: string, data: UpdateApiKeyRequest): Promise<ApiKeyListItem> {
  return fetchWithAuth(`/api/v1/api-keys/${apiKeyId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

async function activateApiKey(apiKeyId: string): Promise<ApiKeyListItem> {
  return fetchWithAuth(`/api/v1/api-keys/${apiKeyId}/activate`, {
    method: 'POST',
  })
}

async function deactivateApiKey(apiKeyId: string): Promise<ApiKeyListItem> {
  return fetchWithAuth(`/api/v1/api-keys/${apiKeyId}/deactivate`, {
    method: 'POST',
  })
}

async function deleteApiKey(apiKeyId: string): Promise<void> {
  return fetchWithAuth(`/api/v1/api-keys/${apiKeyId}`, {
    method: 'DELETE',
  })
}

export function useApiKeys(skip: number = 0, limit: number = 100) {
  return useQuery({
    queryKey: ['apiKeys', skip, limit],
    queryFn: () => listApiKeys(skip, limit),
  })
}

export function useCreateApiKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })
}

export function useUpdateApiKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ apiKeyId, data }: { apiKeyId: string; data: UpdateApiKeyRequest }) =>
      updateApiKey(apiKeyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })
}

export function useActivateApiKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: activateApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })
}

export function useDeactivateApiKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deactivateApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })
}

export function useDeleteApiKey() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteApiKey,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] })
    },
  })
}

